import { AddImageIcon, CircleCloseIcon } from 'assets/icons';
import { CustomButton, CustomModal, CustomTextArea } from 'components';
import { FILE_TYPE, PRESIGNED_UPLOAD_USE_CASE } from 'constants/common';
import { FC, useEffect, useState } from 'react';
import { AttachmentFile, UploadAttachmentFile, VoidFn } from 'types/baseTypes';
import { useCreateNoteMutation, useEditNoteMutation } from '../api';
import MultiFileUpload from 'components/multi-file-upload/MultiFileUpload';
import { ButtonType } from 'components/custom-button/CustomButton';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';
import { notesMaxCount } from 'containers/settings/constants';

export interface JobNote {
  noteId: number;
  subTitle: string;
  attachments: AttachmentFile[];
}

interface CreateEditNoteModalProps {
  defaultNote?: string;
  onClose: VoidFn;
  isEdit: boolean;
  currentNote: JobNote;
  teamId: number;
  jobId: string;
  getJobTimeline: VoidFn;
  setNotesInput?: (note: string) => void;
}

const CreateEditNoteModal: FC<CreateEditNoteModalProps> = ({
  defaultNote = '',
  onClose,
  isEdit,
  currentNote,
  teamId,
  jobId,
  getJobTimeline,
  setNotesInput
}) => {
  const [comment, setComment] = useState(defaultNote);
  const [noteAttachments, setNoteAttachments] = useState<UploadAttachmentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadAbortController] = useState(() => new AbortController());
  const [createNoteMutation, createNoteState] = useCreateNoteMutation();
  const { isLoading: isCreateNoteLoading, isSuccess: isCreateNoteSuccess } = createNoteState;
  const [editNoteMutation, editNoteState] = useEditNoteMutation();
  const { isLoading: isEditNoteLoading, isSuccess: isEditNoteSuccess } = editNoteState;

  useEffect(() => {
    if (!isEdit) logAnalyticEvent(JobsCleverTapEvents.webCreateNotePage);
  }, []);

  useEffect(() => {
    if (isEdit && currentNote) {
      setComment(currentNote.subTitle);
      setNoteAttachments((currentNote.attachments ?? []) as UploadAttachmentFile[]);
    }
  }, [currentNote, isEdit]);

  useEffect(() => {
    if ((!isCreateNoteLoading && isCreateNoteSuccess) || (!isEditNoteLoading && isEditNoteSuccess)) {
      getJobTimeline();
      setNotesInput?.('');
      onClose();
    }
  }, [isCreateNoteLoading, isCreateNoteSuccess, isEditNoteLoading, isEditNoteSuccess]);

  const formatAttachments = (): AttachmentFile[] => {
    return noteAttachments.map(file => ({
      id: file.id,
      title: file.title,
      url: file.url,
      description: file.description ?? '',
      urlLink: file.urlLink
    }));
  };

  const onSaveClick = () => {
    if (isEdit) {
      editNoteMutation({
        comment: comment.trim(),
        attachments: formatAttachments(),
        noteId: currentNote.noteId,
        teamId,
        jobId
      });
    } else {
      createNoteMutation({ comment: comment.trim(), attachments: formatAttachments(), jobId, teamId });
    }
  };

  const onFileRemove = (id: number | string) => {
    const updatedAttachments = noteAttachments.filter(attachment => attachment.id !== id && attachment.tempId !== id);
    setNoteAttachments(updatedAttachments);
  };

  const handleClose = () => {
    uploadAbortController.abort();
    onClose();
  };

  const isSaveDisabled = () => {
    if (isUploading) return true;
    if (isEdit) {
      if (comment.trim() !== currentNote.subTitle.trim()) return false;
      const currentAttachments = currentNote?.attachments ?? [];
      if (noteAttachments.length !== currentAttachments.length) return false;
      for (let i = 0; i < noteAttachments.length; i++) {
        if (noteAttachments[i].url !== currentAttachments[i].url) return false;
      }
    } else if (comment || noteAttachments.length > 0) return false;
    return true;
  };

  return (
    <CustomModal open={true} width="md:w-[610px]" height="md:min-h-[440px] md:max-h-[668px]">
      <div className="relative flex grow flex-col">
        <CircleCloseIcon onClick={handleClose} className="absolute right-2 top-2 shrink-0" />

        <div className="flex items-center justify-between  px-10 pt-9">
          <div className="text-xl font-bold text-headingGray">{isEdit ? 'Edit note' : 'Create note'}</div>
        </div>
        <div className="mt-4 w-full border-b border-borderGray" />
        <div className="customNormalScroll max-h-[510px] overflow-auto px-10 pt-4 pb-2.5">
          <div className="mb-2 text-sm font-semibold text-primaryText">Note</div>
          <CustomTextArea
            id={isEdit ? 'edit-note-text-input' : 'create-note-text-input'}
            name="note-input"
            placeholder="Enter note here"
            className="h-[85px] py-3 px-5 focus:outline-none"
            value={comment}
            onChange={setComment}
            maxLength={notesMaxCount}
          />
          <div className="mt-4 mb-2 text-sbase font-semibold text-primaryText">Attachments</div>
          <MultiFileUpload
            id={isEdit ? 'edit-note-add-attachment-card' : 'create-note-add-attachment-card'}
            setFiles={setNoteAttachments}
            files={noteAttachments}
            onFileRemove={onFileRemove}
            className="mb-5"
            height="h-[100px]"
            FileUploadIcon={AddImageIcon}
            supportedFileTypes={{
              [FILE_TYPE.JPG]: [],
              [FILE_TYPE.JPEG]: [],
              [FILE_TYPE.PNG]: [],
              [FILE_TYPE.WEBP]: []
            }}
            teamId={teamId.toString()}
            useCase={PRESIGNED_UPLOAD_USE_CASE.JOBS}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadToPresigned={true}
            uploadAbortSignal={uploadAbortController.signal}
            previewClassName="max-h-[188px]"
          />
        </div>
        <div className="flex items-center justify-end border-t border-borderGray py-4 pr-10">
          <CustomButton
            id="edit-job-schedule-cancel-button"
            type={ButtonType.SECONDARY}
            onClick={handleClose}
            className="mr-5 w-20">
            Cancel
          </CustomButton>
          <CustomButton
            id="edit-job-schedule-save-button"
            type={ButtonType.PRIMARY}
            onClick={onSaveClick}
            disabled={isSaveDisabled()}
            className="w-20"
            isLoading={isCreateNoteLoading || isEditNoteLoading}>
            {isEdit ? 'Save' : 'Add'}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateEditNoteModal;
